import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  UsersThree, Heart, Sparkle, MagnifyingGlass, Star, MapPin, Briefcase, X, Check, Trash, UserCirclePlus,
} from "@phosphor-icons/react";

/* Find Friends Hub — public page. Browsing is 100% free.
 * Friend requests + profile promotion cost admin-configured Credits. */

const GOAL_TINTS = {
  "Dating": "bg-pink-500/20 text-pink-200 border-pink-500/40",
  "Marriage": "bg-red-500/20 text-red-200 border-red-500/40",
  "Relationship": "bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-500/40",
  "Friendship": "bg-sky-500/20 text-sky-200 border-sky-500/40",
  "Study Mate": "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
  "Business Networking": "bg-amber-500/20 text-amber-200 border-amber-500/40",
};

export default function FindFriends() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [seekers, setSeekers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [goalFilter, setGoalFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [myProfile, setMyProfile] = useState(null);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [form, setForm] = useState({
    contact_number: "", occupation: "", relationship_goal: "Friendship",
    location: "", bio: "",
  });
  const [saving, setSaving] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [gateOpen, setGateOpen] = useState(false); // "complete your seeker profile" nag
  const [deleting, setDeleting] = useState(false);  const loadRequests = async () => {
    try {
      const { data } = await api.get("/find-friends/requests");
      setIncoming(data.incoming || []);
      setOutgoing(data.outgoing || []);
    } catch {
      /* not fatal */
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (goalFilter !== "all") params.set("goal", goalFilter);
      if (query.trim()) params.set("q", query.trim());
      const [c, list] = await Promise.all([
        api.get("/find-friends/config"),
        api.get(`/find-friends/seekers?${params.toString()}`),
      ]);
      setCfg(c.data);
      setSeekers(list.data || []);
      // Pre-fill form from own profile if present.
      const mine = (list.data || []).find(s => s.user_id === user?.user_id);
      if (mine) {
        setMyProfile(mine);
        setForm({
          contact_number: mine.contact_number || "",
          occupation: mine.occupation || "",
          relationship_goal: mine.relationship_goal || "Friendship",
          location: mine.location || "",
          bio: mine.bio || "",
        });
      }
    } catch (e) {
      toast.error("Failed to load Find Friends");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); loadRequests(); /* eslint-disable-next-line */ }, [goalFilter]);

  const displayedSeekers = seekers;

  const respondRequest = async (req, accept) => {
    if (accept && cfg) {
      // Phase 1: acceptance costs the RECEIVER (me) the admin-configured
      // credit amount. Confirm before charging.
      const acceptCost = Number(cfg.friend_request_acceptance_credit_cost || 0);
      const senderReward = Math.floor(acceptCost / 2);
      const confirmMsg = acceptCost > 0
        ? `Accepting this friend request will use ${acceptCost} Credits. `
          + `The sender will earn ${senderReward} Credits. Continue?`
        : "Accept this friend request?";
      if (!window.confirm(confirmMsg)) return;
    }
    try {
      const { data } = await api.post(`/find-friends/request/${req.request_id}/respond`, { accept });
      if (accept && data.chat_url) {
        const rewardBit = data.sender_reward
          ? ` · ${data.sender_reward} Credits sent to sender`
          : "";
        toast.success(`Accepted — opening chat${rewardBit}`);
        nav(data.chat_url);
      } else {
        toast.success(accept ? "Accepted" : "Declined");
        loadRequests();
      }
      refresh?.();
    } catch (e) {
      // Phase 1: insufficient-credits path.
      const d = e.response?.data?.detail;
      if (e.response?.status === 402 && typeof d === "object" && d?.code === "insufficient_credits") {
        toast.error(
          `Need ${d.cost} Credits to accept. You have ${d.remaining}.`,
          {
            duration: 6000,
            action: {
              label: "Get Credits",
              onClick: () => nav("/buy-points"),
            },
          },
        );
        return;
      }
      toast.error(typeof d === "string" ? d : "Failed");
    }
  };

  const save = async () => {
    if (!user?.user_id) {
      toast.error("Please sign in to post a seeker profile.");
      nav("/");
      return;
    }
    if (!form.relationship_goal) { toast.error("Pick a relationship goal."); return; }
    setSaving(true);
    try {
      const r = await api.post("/find-friends/seeker", form);
      setMyProfile(r.data);
      toast.success(myProfile ? "Profile updated" : "You're on the Seekers list!");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const promote = async () => {
    if (!cfg) return;
    if (!window.confirm(`Feature your profile at the top for ${cfg.seeker_promotion_duration_hours}h — costs ${cfg.seeker_promotion_credit_cost} Credits?`)) return;
    setPromoting(true);
    try {
      const r = await api.post("/find-friends/seeker/promote");
      toast.success(`Promoted until ${new Date(r.data.promoted_until).toLocaleString()}`);
      refresh?.();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to promote");
    } finally { setPromoting(false); }
  };

  const sendRequest = async (uid) => {
    // Feb 2026 — sender must have posted their own seeker profile first.
    // Without this gate, a user can accumulate friends without ever
    // exposing anything about themselves.
    if (!myProfile) {
      setGateOpen(true);
      return;
    }
    try {
      await api.post("/find-friends/request", { recipient_id: uid });
      toast.success("Friend request sent.");
      refresh?.();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    }
  };

  const deleteMyProfile = async () => {
    if (!window.confirm("Delete your friend-seeker profile? You will be removed from the directory and can post again anytime.")) return;
    setDeleting(true);
    try {
      await api.delete("/find-friends/seeker");
      toast.success("Your seeker profile has been deleted.");
      setMyProfile(null);
      setForm({ contact_number: "", occupation: "",
                 relationship_goal: "Friendship", location: "", bio: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    } finally { setDeleting(false); }
  };

  const cancelRequest = async (reqId) => {
    try {
      await api.post("/find-friends/request/cancel", { request_id: reqId });
      toast.success("Request cancelled.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel");
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-6 pt-20 md:pt-8" data-testid="find-friends-page">
      <header className="space-y-2">
        <div className="flex items-center gap-2 text-fuchsia-400 text-xs uppercase tracking-widest font-bold">
          <UsersThree size={14} weight="fill"/> Find Friends
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-black leading-tight">
          Meet people, study mates & business partners.
        </h1>
        <p className="text-white/70 text-sm max-w-2xl">
          Browsing this Hub is 100% free. Post your own seeker profile in seconds.
          Sending friend requests is free. Accepting a request uses admin-configured Credits — the sender earns 50% as a reward.
        </p>
      </header>

      {/* Incoming friend requests — priority surface */}
      {incoming.length > 0 && (
        <Card className="bg-emerald-950/30 border-emerald-500/30" data-testid="incoming-requests">
          <CardContent className="p-4 md:p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Heart size={18} weight="fill" className="text-emerald-400" />
              <h3 className="font-heading font-bold text-lg">
                Incoming friend requests · {incoming.length}
              </h3>
            </div>
            <div className="space-y-2">
              {incoming.map((r) => (
                <div
                  key={r.request_id}
                  data-testid={`incoming-req-${r.request_id}`}
                  className="flex items-center gap-3 bg-black/30 rounded-lg p-3"
                >
                  {r.sender?.picture ? (
                    <img src={r.sender.picture} alt="" className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center font-black">
                      {(r.sender?.name || "?")[0]}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold truncate">@{r.sender?.name || "user"}</div>
                    {r.message && <div className="text-xs text-white/60 line-clamp-2">"{r.message}"</div>}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-white/20"
                    onClick={() => respondRequest(r, false)}
                    data-testid={`req-decline-${r.request_id}`}
                  >
                    <X size={12} weight="bold" />
                  </Button>
                  <Button
                    size="sm"
                    className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold"
                    onClick={() => respondRequest(r, true)}
                    data-testid={`req-accept-${r.request_id}`}
                    title={cfg?.friend_request_acceptance_credit_cost
                      ? `Accepting uses ${cfg.friend_request_acceptance_credit_cost} Credits`
                      : "Accept"}
                  >
                    <Check size={12} weight="bold" className="mr-1" />
                    Accept
                    {cfg?.friend_request_acceptance_credit_cost > 0 && (
                      <span className="ml-1 text-[10px] font-heading font-black opacity-80">
                        ({cfg.friend_request_acceptance_credit_cost} cr)
                      </span>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Seeker profile form */}
      <Card className="bg-[#111] border-white/10">
        <CardContent className="p-4 md:p-5 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="font-heading font-bold text-lg flex items-center gap-2">
              <Heart size={18} weight="fill" className="text-pink-400"/> Your seeker profile
            </h3>
            {myProfile?.is_promoted && (
              <Badge className="bg-amber-500/20 text-amber-200 border-amber-500/40 gap-1">
                <Sparkle size={11} weight="fill"/> Featured
              </Badge>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/50 block mb-1">Contact number</label>
              <Input data-testid="seeker-contact" value={form.contact_number} onChange={e=>setForm({...form, contact_number: e.target.value})} className="bg-black/40 border-white/10" placeholder="+233 …" />
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1">Occupation / Field</label>
              <Input data-testid="seeker-occupation" value={form.occupation} onChange={e=>setForm({...form, occupation: e.target.value})} className="bg-black/40 border-white/10" placeholder="e.g. Law student, Software Engineer" />
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1">Relationship goal</label>
              <Select value={form.relationship_goal} onValueChange={v=>setForm({...form, relationship_goal: v})}>
                <SelectTrigger data-testid="seeker-goal" className="bg-black/40 border-white/10"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                  {(cfg?.relationship_goals || []).map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1">Location / Area</label>
              <Input data-testid="seeker-location" value={form.location} onChange={e=>setForm({...form, location: e.target.value})} className="bg-black/40 border-white/10" placeholder="e.g. Accra, Ghana" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-white/50 block mb-1">Short bio / About me</label>
              <Textarea data-testid="seeker-bio" value={form.bio} onChange={e=>setForm({...form, bio: e.target.value})} className="bg-black/40 border-white/10 min-h-[80px]" placeholder="Tell people what you're looking for…" />
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="seeker-save-btn" onClick={save} disabled={saving} className="bg-fuchsia-500 hover:bg-fuchsia-600 text-black font-heading font-bold">
              {saving ? "Saving…" : myProfile ? "Update Profile" : "Post Request · Join Seekers List"}
            </Button>
            {myProfile && (
              <Button data-testid="seeker-promote-btn" onClick={promote} disabled={promoting} variant="outline" className="border-amber-500/40 text-amber-300 gap-1">
                <Sparkle size={14} weight="fill"/>
                {promoting ? "Promoting…" : `Feature Me (${cfg?.seeker_promotion_credit_cost ?? 0} cr / ${cfg?.seeker_promotion_duration_hours ?? 0}h)`}
              </Button>
            )}
            {myProfile && (
              <Button data-testid="seeker-delete-btn" onClick={deleteMyProfile}
                       disabled={deleting} variant="outline"
                       className="border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 gap-1">
                <Trash size={14} weight="bold"/>
                {deleting ? "Deleting…" : "Delete My Profile"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-center">
        <Select value={goalFilter} onValueChange={setGoalFilter}>
          <SelectTrigger className="bg-black/40 border-white/10 w-56" data-testid="seeker-filter-goal"><SelectValue/></SelectTrigger>
          <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
            <SelectItem value="all">All goals</SelectItem>
            {(cfg?.relationship_goals || []).map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1 min-w-[200px] relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40" />
          <Input value={query} onChange={e=>setQuery(e.target.value)} onKeyDown={e => e.key === "Enter" && load()} placeholder="Search occupation / location…" className="pl-8 bg-black/40 border-white/10" data-testid="seeker-search-input"/>
        </div>
        <Button variant="outline" className="border-white/20" onClick={load} data-testid="seeker-search-btn">Search</Button>
      </div>

      {/* Seekers directory */}
      {loading && <div className="text-white/50 text-sm">Loading seekers…</div>}
      {!loading && displayedSeekers.length === 0 && (
        <div className="text-white/50 text-sm" data-testid="seeker-empty">
          No seekers match yet. Be the first!
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-3">
        {displayedSeekers.map(s => (
          <Card key={s.user_id} className={`bg-[#111] border ${s.is_promoted ? "border-amber-500/50 shadow-lg shadow-amber-500/10" : "border-white/10"}`} data-testid={`seeker-card-${s.user_id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-start gap-3">
                <button onClick={() => nav(`/u/${s.user_id}`)} className="shrink-0" data-testid={`seeker-avatar-${s.user_id}`}>
                  {s.picture ? (
                    <img src={s.picture} alt="" className="w-12 h-12 rounded-full object-cover"/>
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-fuchsia-500/20 flex items-center justify-center font-black">
                      {(s.name || "?")[0]}
                    </div>
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <button onClick={() => nav(`/u/${s.user_id}`)} className="text-left w-full">
                    <div className="font-heading font-bold truncate">{s.name}</div>
                  </button>
                  <div className="text-xs text-white/60 space-y-0.5 mt-0.5">
                    {s.occupation && <div className="flex items-center gap-1"><Briefcase size={11}/> {s.occupation}</div>}
                    {s.location && <div className="flex items-center gap-1"><MapPin size={11}/> {s.location}</div>}
                  </div>
                </div>
                {s.is_promoted && <Star size={16} weight="fill" className="text-amber-300"/>}
              </div>
              <div className="flex flex-wrap gap-1">
                <Badge className={`text-[10px] border ${GOAL_TINTS[s.relationship_goal] || "bg-white/10 border-white/20"}`}>{s.relationship_goal}</Badge>
              </div>
              {s.bio && <p className="text-xs text-white/70 line-clamp-3">{s.bio}</p>}
              {s.user_id !== user?.user_id && (
                <div className="pt-1">
                  {s.friendship_status === "friends" ? (
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-emerald-500/40 bg-emerald-500/10 text-emerald-300 font-bold pointer-events-none"
                        data-testid={`seeker-friends-${s.user_id}`}
                      >
                        <Check size={12} weight="bold" className="mr-1"/> Friends
                      </Button>
                      <Button
                        size="sm"
                        className="bg-sky-500 hover:bg-sky-600 text-black font-bold"
                        onClick={() => nav(`/inbox?with=${s.user_id}`)}
                        data-testid={`seeker-chat-${s.user_id}`}
                      >
                        Chat
                      </Button>
                    </div>
                  ) : s.my_pending_request_id ? (
                    <Button size="sm" variant="outline" className="w-full border-white/20" onClick={() => cancelRequest(s.my_pending_request_id)} data-testid={`seeker-cancel-${s.user_id}`}>
                      <X size={12} weight="bold" className="mr-1"/> Cancel Request
                    </Button>
                  ) : (
                    <Button size="sm" className="w-full bg-fuchsia-500 hover:bg-fuchsia-600 text-black font-bold" onClick={() => sendRequest(s.user_id)} data-testid={`seeker-request-${s.user_id}`}>
                      <Check size={12} weight="bold" className="mr-1"/> Send Request
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      <Dialog open={gateOpen} onOpenChange={setGateOpen}>
        <DialogContent
          data-testid="seeker-gate-dialog"
          className="bg-gradient-to-br from-fuchsia-950/70 via-[#0e0e0e] to-amber-950/40
                     border border-fuchsia-500/30 max-w-md w-[95vw] sm:w-auto shadow-2xl shadow-fuchsia-500/20"
        >
          <DialogHeader>
            <div className="mx-auto w-14 h-14 rounded-2xl bg-fuchsia-500/20 border border-fuchsia-500/40 grid place-items-center mb-2">
              <UserCirclePlus size={30} weight="fill" className="text-fuchsia-300"/>
            </div>
            <DialogTitle className="font-heading text-2xl sm:text-3xl font-black text-center">
              Complete your Friend-Seeker profile first
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-white/85 text-center leading-relaxed">
            Before you can send a friend request, please post your own
            <b className="text-fuchsia-300"> Friend-Seeker profile</b> so the person
            you're reaching out to knows a little about you. It's free and takes
            under a minute.
          </p>
          <DialogFooter className="sm:justify-center gap-2 mt-2">
            <Button variant="outline"
                     onClick={() => setGateOpen(false)}
                     className="border-white/20 bg-transparent hover:bg-white/5">
              Not now
            </Button>
            <Button
              data-testid="seeker-gate-cta"
              onClick={() => {
                setGateOpen(false);
                document.querySelector('[data-testid="seeker-save-btn"]')
                  ?.scrollIntoView({ behavior: "smooth", block: "center" });
              }}
              className="bg-gradient-to-r from-fuchsia-500 to-amber-400 hover:opacity-95 text-black font-heading font-bold shadow-lg shadow-fuchsia-500/30 gap-1"
            >
              <Sparkle size={16} weight="fill"/>
              Complete my profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
